'use client';

import Link from 'next/link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import {
  leadMagnetSchema,
  type LeadMagnetInput,
} from '@/lib/schemas/lead-magnet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics/ym';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-sm text-destructive">{message}</p>;
}

export function EmailGateForm() {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LeadMagnetInput>({
    resolver: zodResolver(leadMagnetSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
    } as Partial<LeadMagnetInput> as LeadMagnetInput,
  });

  const onSubmit = async (data: LeadMagnetInput) => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/lead-magnet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.status === 429) {
        toast.warning('Уже отправляли — проверьте почту через пару минут');
        return;
      }
      if (!res.ok) {
        toast.error('Что-то сломалось, напишите в Telegram');
        return;
      }
      toast.success('Отправил на почту. Проверьте «Промоакции» / «Спам».');
      track.formSubmit({ form: 'lead_magnet' });
      track.leadMagnetDownload('email_gate');
      reset();
    } catch {
      toast.error('Что-то сломалось, напишите в Telegram');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
      <div className="grid gap-2">
        <Label htmlFor="lm-email">
          Email — пришлю PDF + 1 письмо с разбором
        </Label>
        <Input
          id="lm-email"
          type="email"
          autoComplete="email"
          placeholder="name@company.ru"
          aria-invalid={!!errors.email}
          {...register('email')}
        />
        <FieldError message={errors.email?.message} />
      </div>

      <div className="flex items-start gap-3">
        <input
          id="lm-consent"
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-foreground"
          aria-invalid={!!errors.consent}
          {...register('consent')}
        />
        <Label
          htmlFor="lm-consent"
          className="text-sm text-muted-foreground leading-snug"
        >
          <span>
            Согласен на обработку персональных данных по{' '}
            <Link
              href="/privacy"
              className="underline underline-offset-4 hover:text-foreground"
            >
              152-ФЗ
            </Link>
          </span>
        </Label>
      </div>
      <FieldError message={errors.consent?.message} />

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          buttonVariants({ size: 'lg' }),
          'min-h-12 w-full text-base md:w-auto',
        )}
      >
        {submitting ? 'Отправляю…' : 'Получить чек-лист'}
      </button>
    </form>
  );
}
